<script setup>
import { ref, computed, onBeforeUnmount } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import VueHcaptcha from '@hcaptcha/vue3-hcaptcha';
import { resendConfirmation } from '../../../api/auth';
import SiteFooter from '../SiteFooter.vue';

const props = defineProps({
  email: {
    type: String,
    default: '',
  },
});

const router = useRouter();
const store = useStore();

if (!props.email) {
  router.push({ name: 'login' });
}

const globalConfig = computed(() => store.getters['globalConfig/get']);
const RESEND_COOLDOWN_SECONDS = 30;

const isResendingEmail = ref(false);
const resendCooldown = ref(0);
const hCaptcha = ref(null);
let captchaToken = '';
let cooldownTimer = null;

const startCooldown = () => {
  resendCooldown.value = RESEND_COOLDOWN_SECONDS;
  if (cooldownTimer) clearInterval(cooldownTimer);
  cooldownTimer = setInterval(() => {
    resendCooldown.value -= 1;
    if (resendCooldown.value <= 0) {
      clearInterval(cooldownTimer);
      cooldownTimer = null;
    }
  }, 1000);
};

const performResend = async () => {
  isResendingEmail.value = true;
  try {
    await resendConfirmation({
      email: props.email,
      hCaptchaClientResponse: captchaToken,
    });
    useAlert('Verification email re-sent — please check your inbox.');
    startCooldown();
  } catch {
    useAlert('Could not resend the verification email. Please try again.');
  } finally {
    isResendingEmail.value = false;
    captchaToken = '';
    if (globalConfig.value.hCaptchaSiteKey) {
      hCaptcha.value.reset();
    }
  }
};

const handleResendEmail = () => {
  if (isResendingEmail.value || resendCooldown.value > 0) return;
  if (globalConfig.value.hCaptchaSiteKey) {
    hCaptcha.value.execute();
  } else {
    performResend();
  }
};

const onCaptchaVerified = token => {
  captchaToken = token;
  performResend();
};

const onCaptchaError = () => {
  isResendingEmail.value = false;
  captchaToken = '';
  hCaptcha.value.reset();
};

const goToSignup = () => {
  // Route is named 'auth_signup' (NOT 'signup') — the old name silently failed
  // the navigation, so "Change email address" did nothing. Carry the current
  // (possibly mistyped) email along via router state (not query → keeps the
  // email out of the URL) so the signup form can pre-fill + let the user edit it.
  router.push({ name: 'auth_signup', state: { email: props.email } });
};

onBeforeUnmount(() => {
  if (cooldownTimer) clearInterval(cooldownTimer);
});
</script>

<template>
  <div class="cc-verify-page">
    <div class="cc-verify-noise" />

    <header class="cc-verify-header">
      <div class="cc-brand-mark">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="38"
          height="38"
          viewBox="0 0 96 92"
          class="cc-brand-icon"
        >
          <defs>
            <linearGradient id="goldgrad-vfy" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0" stop-color="#d8b86a" />
              <stop offset="0.5" stop-color="#a8842f" />
              <stop offset="1" stop-color="#7a5a14" />
            </linearGradient>
            <clipPath id="clipover-vfy">
              <rect x="38" y="44" width="20" height="22" />
            </clipPath>
          </defs>
          <path
            d="M 11 0 L 33 0 A 11 11 0 0 1 44 11 L 44 25 A 11 11 0 0 1 33 36 L 24 36 L 7 47 L 12 36 L 11 36 A 11 11 0 0 1 0 25 L 0 11 A 11 11 0 0 1 11 0 Z"
            transform="translate(7,11)"
            fill="none"
            stroke="#0d9488"
            stroke-width="7"
            stroke-linejoin="round"
          />
          <g transform="translate(7,11)" fill="#0d9488">
            <circle cx="15" cy="18" r="2.4" />
            <circle cx="24" cy="18" r="2.4" />
            <circle cx="33" cy="18" r="2.4" />
          </g>
          <path
            d="M 11 0 L 33 0 A 11 11 0 0 1 44 11 L 44 25 A 11 11 0 0 1 33 36 L 32 36 L 37 47 L 20 36 L 11 36 A 11 11 0 0 1 0 25 L 0 11 A 11 11 0 0 1 11 0 Z"
            transform="translate(41,30)"
            fill="none"
            stroke="url(#goldgrad-vfy)"
            stroke-width="7"
            stroke-linejoin="round"
          />
          <g transform="translate(41,30)" fill="#a8842f">
            <circle cx="15" cy="18" r="2.4" />
            <circle cx="24" cy="18" r="2.4" />
            <circle cx="33" cy="18" r="2.4" />
          </g>
          <g clip-path="url(#clipover-vfy)">
            <path
              d="M 11 0 L 33 0 A 11 11 0 0 1 44 11 L 44 25 A 11 11 0 0 1 33 36 L 24 36 L 7 47 L 12 36 L 11 36 A 11 11 0 0 1 0 25 L 0 11 A 11 11 0 0 1 11 0 Z"
              transform="translate(7,11)"
              fill="none"
              stroke="#0d9488"
              stroke-width="7"
              stroke-linejoin="round"
            />
          </g>
        </svg>
        <div class="cc-brand-text">
          <div class="cc-wordmark">
            <span class="cc-wordmark-part cc-wordmark-creator">Creator</span><span class="cc-wordmark-dash">-</span><span class="cc-wordmark-part cc-wordmark-chat">Chat</span><span class="cc-wordmark-tld">.com</span>
          </div>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="90"
            height="2"
            viewBox="0 0 90 2"
            class="cc-brand-divider"
          >
            <defs>
              <linearGradient id="tealfade-vfy" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stop-color="#0d9488" stop-opacity="0" />
                <stop offset="20%" stop-color="#0d9488" stop-opacity="1" />
                <stop offset="80%" stop-color="#0d9488" stop-opacity="1" />
                <stop offset="100%" stop-color="#0d9488" stop-opacity="0" />
              </linearGradient>
            </defs>
            <rect x="0" y="0" width="90" height="2" fill="url(#tealfade-vfy)" />
          </svg>
          <div class="cc-tagline">Conversations · Tailored</div>
        </div>
      </div>
    </header>

    <main class="cc-verify-main">
      <div class="cc-verify-card">
        <div class="cc-envelope">
          <svg
            width="34"
            height="34"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path
              d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"
            />
            <polyline points="22,6 12,13 2,6" />
          </svg>
        </div>

        <h1 class="cc-h-display">Verify your email to activate your account</h1>
        <p class="cc-sub">We sent a verification link to</p>
        <p class="cc-email">{{ email || 'your email' }}</p>

        <div class="cc-warning">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#92400e"
            stroke-width="2"
            stroke-linecap="round"
            class="cc-warning-icon"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <div class="cc-warning-text">
            <strong>Your account is not active yet.</strong> Click the verification link in the email we just sent to activate your account. The link expires in 24 hours.
          </div>
        </div>

        <VueHcaptcha
          v-if="globalConfig.hCaptchaSiteKey"
          ref="hCaptcha"
          size="invisible"
          :sitekey="globalConfig.hCaptchaSiteKey"
          @verify="onCaptchaVerified"
          @error="onCaptchaError"
          @expired="onCaptchaError"
          @challenge-expired="onCaptchaError"
          @closed="onCaptchaError"
        />

        <button
          type="button"
          class="cc-btn-primary"
          :disabled="isResendingEmail || resendCooldown > 0"
          @click="handleResendEmail"
        >
          <template v-if="isResendingEmail">Resending…</template>
          <template v-else-if="resendCooldown > 0">
            Resend available in {{ resendCooldown }}s
          </template>
          <template v-else>Resend verification email</template>
        </button>

        <div class="cc-actions">
          <button type="button" class="cc-link-btn" @click="goToSignup">
            Change email address
          </button>
        </div>
      </div>

      <div class="cc-help-line">
        Didn't receive it? Check your spam folder or wait a minute.
      </div>
    </main>

    <SiteFooter />
  </div>
</template>

<style scoped>
.cc-verify-page {
  position: relative;
  min-height: 100vh;
  width: 100%;
  background: #efeae0;
  color: #0b1414;
  font-family: 'Inter', sans-serif;
  display: flex;
  flex-direction: column;
}
.cc-verify-noise {
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 0;
  opacity: 0.35;
  mix-blend-mode: multiply;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0.4 0 0 0 0 0.35 0 0 0 0 0.25 0 0 0 0.05 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>");
}

.cc-verify-header {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 22px 36px;
}

.cc-brand-mark { display: flex; align-items: center; }
.cc-brand-icon { display: block; margin-right: 14px; }
.cc-brand-text { line-height: 1; }
.cc-wordmark { line-height: 21px; white-space: nowrap; }
.cc-wordmark-part {
  font-family: 'Fraunces', 'Times New Roman', Georgia, serif;
  font-size: 21px;
  letter-spacing: 1.26px;
  text-transform: uppercase;
  color: #0b1414;
}
.cc-wordmark-creator { font-weight: 400; }
.cc-wordmark-chat { font-weight: 300; }
.cc-wordmark-dash {
  font-family: 'Fraunces', 'Times New Roman', Georgia, serif;
  font-size: 21px;
  letter-spacing: 1.26px;
  color: #a8842f;
  font-weight: 400;
}
.cc-wordmark-tld {
  font-family: 'Inter', 'Helvetica Neue', Arial, sans-serif;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: lowercase;
  color: #a8842f;
  vertical-align: 8px;
  padding-left: 2px;
}
.cc-brand-divider { display: block; margin: 6px 0 5px 0; }
.cc-tagline {
  font-family: 'Inter', 'Helvetica Neue', Arial, sans-serif;
  font-size: 8.5px;
  font-weight: 500;
  letter-spacing: 2.56px;
  color: rgba(11, 20, 20, 0.5);
  text-transform: uppercase;
  line-height: 1;
}

.cc-verify-main {
  position: relative;
  z-index: 1;
  flex: 1;
  max-width: 520px;
  width: 100%;
  margin: 60px auto 60px;
  padding: 0 24px;
  box-sizing: border-box;
}

.cc-verify-card {
  background: #ffffff;
  border: 1px solid #e0d8c5;
  border-radius: 16px;
  padding: 40px 36px;
  text-align: center;
}

.cc-envelope {
  width: 72px;
  height: 72px;
  margin: 0 auto 20px;
  border-radius: 18px;
  background: rgba(13, 148, 136, 0.22);
  color: #0f766e;
  display: flex;
  align-items: center;
  justify-content: center;
}

.cc-h-display {
  font-family: 'Cinzel', 'Fraunces', 'Times New Roman', Georgia, serif;
  font-weight: 500;
  color: #0b1414;
  letter-spacing: -0.005em;
  font-size: 27px;
  line-height: 1.25;
  margin: 0 0 10px;
}
.cc-sub {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  color: #6e7570;
  line-height: 1.55;
  margin: 0 0 6px;
}
.cc-email {
  font-family: 'JetBrains Mono', monospace;
  font-size: 14px;
  color: #0b1414;
  font-weight: 600;
  letter-spacing: 0.06em;
  margin: 0 0 22px;
  word-break: break-all;
}

.cc-warning {
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 11px;
  padding: 14px 16px;
  text-align: left;
  margin-bottom: 22px;
  display: flex;
  align-items: flex-start;
  gap: 10px;
}
.cc-warning-icon { flex-shrink: 0; margin-top: 1px; }
.cc-warning-text {
  font-family: 'Inter', sans-serif;
  font-size: 12.5px;
  color: #92400e;
  line-height: 1.55;
}

.cc-btn-primary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 13px;
  border-radius: 10px;
  border: none;
  background: rgba(31, 42, 42, 0.92);
  color: #f5efe0;
  font-family: 'Inter', sans-serif;
  font-size: 14.5px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.12s;
  width: 100%;
}
.cc-btn-primary:hover:not(:disabled) {
  background: rgba(11, 20, 20, 0.98);
  transform: translateY(-1px);
  box-shadow: 0 4px 14px rgba(11, 20, 20, 0.2);
}
.cc-btn-primary:disabled {
  background: #cfc5ac;
  cursor: not-allowed;
}

.cc-actions {
  margin-top: 20px;
  display: flex;
  justify-content: center;
  gap: 18px;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  color: #6e7570;
}
.cc-link-btn {
  background: none;
  border: none;
  color: #6e7570;
  cursor: pointer;
  text-decoration: underline;
  font-size: 12px;
  font-family: 'Inter', sans-serif;
}
.cc-link-btn:hover { color: #0f766e; }

.cc-help-line {
  text-align: center;
  margin-top: 18px;
  font-family: 'Inter', sans-serif;
  font-size: 11.5px;
  color: #6e7570;
}

/* Footer styles live in ../SiteFooter.vue (scoped). */

@media (max-width: 540px) {
  .cc-verify-header { padding: 18px 20px; }
  .cc-verify-main { margin: 30px auto 40px; }
  .cc-verify-card { padding: 32px 24px; }
  .cc-h-display { font-size: 22px; }
  .cc-tagline { display: none; }
}
</style>
